import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Mail,
  Phone,
  Linkedin,
  Eye,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const categoryLabels = {
  ai_integration_engineer: 'AI Integration Engineer',
  ai_application_developer: 'AI Application Developer',
  ai_automation_engineer: 'AI Automation Engineer',
  prompt_engineer: 'Prompt Engineer',
  ai_workflow_designer: 'AI Workflow Designer',
  ai_qa_engineer: 'AI QA Engineer',
  ai_validation_engineer: 'AI Validation Engineer',
  ai_data_engineer: 'AI Data Engineer',
  ai_rag_engineer: 'AI RAG Engineer',
  ai_devops_engineer: 'AI DevOps Engineer',
  mlops_engineer: 'MLOps Engineer',
  ai_platform_engineer: 'AI Platform Engineer',
  ai_solutions_engineer: 'AI Solutions Engineer',
  ai_support_engineer: 'AI Support Engineer',
  ai_chatbot_engineer: 'AI Chatbot Engineer',
  ai_copilot_engineer: 'AI Copilot Engineer',
  ai_api_engineer: 'AI API Engineer',
  ai_security_engineer: 'AI Security Engineer',
  ai_governance_engineer: 'AI Governance Engineer',
  ai_compliance_analyst: 'AI Compliance Analyst',
  ai_risk_analyst: 'AI Risk Analyst',
  ai_model_monitoring_engineer: 'AI Model Monitoring Engineer',
  ai_performance_engineer: 'AI Performance Engineer',
  ai_business_analyst: 'AI Business Analyst',
  ai_product_engineer: 'AI Product Engineer',
  ai_implementation_consultant: 'AI Implementation Consultant',
  ai_migration_engineer: 'AI Migration Engineer',
  ai_knowledge_systems_engineer: 'AI Knowledge Systems Engineer',
};

const statusConfig = {
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-700', icon: XCircle },
};

const expertiseLevels = {
  junior: 'Junior',
  mid: 'Mid-Level',
  senior: 'Senior',
  lead: 'Lead',
  principal: 'Principal',
};

export default function TalentManagement() {
  const { isDark } = useTheme();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [talentToDelete, setTalentToDelete] = useState(null);

  useEffect(() => {
    fetchTalents();
    fetchStats();
  }, [statusFilter]);

  const fetchTalents = async () => {
    try {
      const params = statusFilter !== 'all' ? `?approval_status=${statusFilter}` : '';
      const response = await axios.get(`${API}/talents/admin/all${params}`);
      setTalents(response.data);
    } catch (error) {
      toast.error('Failed to fetch talents');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/talents/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async (talentId) => {
    try {
      await axios.post(`${API}/talents/${talentId}/approve`);
      toast.success('Talent approved successfully');
      fetchTalents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to approve talent');
    }
  };

  const handleDecline = async (talentId) => {
    try {
      await axios.post(`${API}/talents/${talentId}/decline`);
      toast.success('Talent declined');
      fetchTalents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to decline talent');
    }
  };

  const handleDelete = async () => {
    if (!talentToDelete) return;
    try {
      await axios.delete(`${API}/talents/${talentToDelete.id}`);
      toast.success('Talent deleted successfully');
      fetchTalents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete talent');
    } finally {
      setDeleteDialogOpen(false);
      setTalentToDelete(null);
    }
  };

  const handleToggleFeatured = async (talent) => {
    try {
      await axios.put(`${API}/talents/${talent.id}`, { is_featured: !talent.is_featured });
      toast.success(talent.is_featured ? 'Removed from featured' : 'Added to featured');
      fetchTalents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update talent');
    }
  };

  const filteredTalents = talents.filter(talent => {
    const matchesSearch = talent.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          talent.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          talent.skills?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="talent-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Talent Management</h1>
          <p className="text-slate-500">Review and manage talent submissions</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total || 0}</p>
              <p className="text-sm text-slate-500">Total Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.pending || 0}</p>
              <p className="text-sm text-slate-500">Pending Review</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.available || 0}</p>
              <p className="text-sm text-slate-500">Available</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.featured || 0}</p>
              <p className="text-sm text-slate-500">Featured</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle>Talent Profiles</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, title, skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                  data-testid="talent-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : filteredTalents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No talents found</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'No talent submissions yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Experience</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTalents.map((talent) => {
                    const status = statusConfig[talent.approval_status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <TableRow key={talent.id} className="hover:bg-slate-50" data-testid={`talent-row-${talent.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                              {talent.display_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{talent.display_name}</p>
                              {talent.is_featured && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Featured</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{talent.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {categoryLabels[talent.category] || talent.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-600">
                            {talent.years_of_experience} yrs ({expertiseLevels[talent.expertise_level] || talent.expertise_level})
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('gap-1', status.className)}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {talent.email && (
                              <a href={`mailto:${talent.email}`} className="text-slate-400 hover:text-blue-600">
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                            {talent.phone && (
                              <a href={`tel:${talent.phone}`} className="text-slate-400 hover:text-blue-600">
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                            {talent.linkedin_url && (
                              <a href={talent.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600">
                                <Linkedin className="w-4 h-4" />
                              </a>
                            )}
                            {!talent.email && !talent.phone && !talent.linkedin_url && (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {formatDate(talent.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTalent(talent);
                                setShowDetailDialog(true);
                              }}
                              data-testid={`view-talent-${talent.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {talent.approval_status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleApprove(talent.id)}
                                  data-testid={`approve-talent-${talent.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDecline(talent.id)}
                                  data-testid={`decline-talent-${talent.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {talent.approval_status === 'approved' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  talent.is_featured 
                                    ? "text-purple-600 hover:text-purple-700" 
                                    : "text-slate-400 hover:text-purple-600"
                                )}
                                onClick={() => handleToggleFeatured(talent)}
                                data-testid={`feature-talent-${talent.id}`}
                              >
                                <Star className={cn("w-4 h-4", talent.is_featured && "fill-current")} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setTalentToDelete(talent);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-talent-${talent.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Talent Detail Dialog */}
      <AlertDialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-lg">
                {selectedTalent?.display_name?.charAt(0) || '?'}
              </div>
              <div>
                <p>{selectedTalent?.display_name}</p>
                <p className="text-sm font-normal text-slate-500">{selectedTalent?.title}</p>
              </div>
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          {selectedTalent && (
            <div className="space-y-4 py-4">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Bio</h4>
                <p className="text-slate-600">{selectedTalent.bio}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Experience</h4>
                  <p className="text-slate-600">{selectedTalent.years_of_experience} years ({expertiseLevels[selectedTalent.expertise_level]})</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Category</h4>
                  <p className="text-slate-600">{categoryLabels[selectedTalent.category] || selectedTalent.category}</p>
                </div>
              </div>

              {selectedTalent.skills?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTalent.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedTalent.certifications?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTalent.certifications.map((cert, idx) => (
                      <Badge key={idx} variant="outline">{cert}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedTalent.industries?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Industries</h4>
                  <p className="text-slate-600">{selectedTalent.industries.join(', ')}</p>
                </div>
              )}

              {(selectedTalent.email || selectedTalent.phone || selectedTalent.linkedin_url) && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
                  <div className="space-y-1 text-slate-600">
                    {selectedTalent.email && <p>Email: {selectedTalent.email}</p>}
                    {selectedTalent.phone && <p>Phone: {selectedTalent.phone}</p>}
                    {selectedTalent.linkedin_url && (
                      <p>LinkedIn: <a href={selectedTalent.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedTalent.linkedin_url}</a></p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            {selectedTalent?.approval_status === 'pending' && (
              <>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => {
                    handleDecline(selectedTalent.id);
                    setShowDetailDialog(false);
                  }}
                >
                  Decline
                </AlertDialogAction>
                <AlertDialogAction
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    handleApprove(selectedTalent.id);
                    setShowDetailDialog(false);
                  }}
                >
                  Approve
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Talent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{talentToDelete?.display_name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
